import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { Card, Empty, ErrorBanner, FormattedDate, Loading, PageHeader } from '../../components/ui'

type Receipt = {
  id: string
  request_id?: string
  material_name?: string
  quantity_received?: number
  unit?: string
  received_at?: number
  vendor_name?: string | null
  notes?: string | null
}

export function MaterialReceiptListPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const q = useQuery<Receipt[]>({
    queryKey: ['material-receipts', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      fetchData<Receipt[]>(orgPath(`/material-receipts?project_id=${projectId}`)),
  })

  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />

  return (
    <div>
      <PageHeader title="Material receipts" subtitle="Recorded inwards against open requests." />
      {!q.data || q.data.length === 0 ? (
        <Empty message="No receipts yet." hint="Receipts are recorded against open material requests." />
      ) : (
        <Card>
          <ul className="divide-y divide-surface-700 text-sm">
            {q.data.map((r) => (
              <li key={r.id} className="py-3 flex justify-between gap-3">
                <div>
                  <div className="font-medium">{r.material_name ?? '—'}</div>
                  {r.vendor_name && (
                    <div className="text-xs text-surface-400">From: {r.vendor_name}</div>
                  )}
                </div>
                <div className="text-right">
                  <div>
                    {r.quantity_received ?? 0} {r.unit ?? ''}
                  </div>
                  <div className="text-xs text-surface-400">
                    <FormattedDate ts={r.received_at ?? null} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
